import { type MutableRefObject, useEffect } from 'react'

/**
 * Keeps the spread on screen: wires the scroller's scroll listener (ruler sync), centers the
 * pasteboard on mount, and re-aligns once real document dimensions are known if no part of the
 * spread is visible (WMPCM-8911).
 */
export function useSpreadVisibility({
  scrollRef,
  pasteboardRef,
  documentCanvasRef,
  zoomRef,
  fitContentDimensions,
  syncRulerScrollTransforms,
  alignFitSpreadInViewport,
  centerSpreadForActualSize,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
  pasteboardRef: MutableRefObject<HTMLDivElement | null>
  documentCanvasRef: MutableRefObject<HTMLDivElement | null>
  zoomRef: MutableRefObject<number>
  fitContentDimensions: { width: number; height: number }
  syncRulerScrollTransforms: () => void
  alignFitSpreadInViewport: () => void
  centerSpreadForActualSize: () => void
}) {
  useEffect(() => {
    const scroller = scrollRef.current
    const board = pasteboardRef.current

    if (!scroller || !board) return

    const onScroll = () => syncRulerScrollTransforms()

    scroller.addEventListener('scroll', onScroll, { passive: true })
    syncRulerScrollTransforms()

    // Center on mount
    const id = window.setTimeout(() => {
      scroller.scrollTo({
        left: Math.max(0, (board.scrollWidth - scroller.clientWidth) / 2),
        top: Math.max(0, (board.scrollHeight - scroller.clientHeight) / 2),
      })
      requestAnimationFrame(() => syncRulerScrollTransforms())
    }, 0)

    return () => {
      scroller.removeEventListener('scroll', onScroll)
      window.clearTimeout(id)
    }
  }, [syncRulerScrollTransforms, scrollRef, pasteboardRef])

  /**
   * Safety net for WMPCM-8911: the "Center on mount" effect above centers on the raw
   * pasteboard midpoint, computed before book settings/document dimensions are necessarily
   * known — and a restored explicit zoom (`editor-zoom-storage`) has no equivalent re-fit path
   * to correct a bad initial guess (only `zoom === 0` fit mode re-aligns on its own when
   * `fitContentDimensions` changes, via `recomputeFitScaleAndAlign`). Either gap can leave the
   * spread scrolled fully out of view after a reload. Re-verify once real document dimensions
   * are known (`fitContentDimensions` changing is the signal — book-wide, so an ordinary spread
   * switch does not retrigger this) and re-align only if no part of the spread is visible, so
   * this never fights a user's deliberate pan/zoom.
   */
  useEffect(() => {
    const scroller = scrollRef.current
    const canvasEl = documentCanvasRef.current

    if (!scroller || !canvasEl) return

    const id = window.setTimeout(() => {
      const sc = scroller.getBoundingClientRect()
      const doc = canvasEl.getBoundingClientRect()
      const isSpreadVisible =
        doc.right > sc.left && doc.left < sc.right && doc.bottom > sc.top && doc.top < sc.bottom

      if (isSpreadVisible) return

      if (zoomRef.current === 0) {
        alignFitSpreadInViewport()
      } else {
        centerSpreadForActualSize()
      }
    }, 0)

    return () => window.clearTimeout(id)
  }, [
    fitContentDimensions,
    alignFitSpreadInViewport,
    centerSpreadForActualSize,
    scrollRef,
    documentCanvasRef,
    zoomRef,
  ])
}
